import { Injectable } from '@angular/core';
import { ApiService } from '@services/api.service';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { LoggerService } from '@services/logger.service';
import { MmssDocumentationModel } from '@/models/tables/mmss-documentation.model';
import { HttpHeaders } from '@angular/common/http';

export interface TableDataApiParams {
  page?: number;
  size?: number;
  sort?: string;
  orderBy?: 'DESC' | 'ASC';
  [key: string]: any; // Allow any additional filter parameters
}

@Injectable({
  providedIn: 'root',
})
export class DocumentationMmssService extends ApiService {
  getDocuments(
    params: TableDataApiParams = {
      page: 1,
      size: 30,
    },
  ) {
    // Filter empty or undefined parameters, keeping all valid filters
    const cleanParams: any = {};

    // Pagination and sorting parameters
    if (params.page !== undefined) cleanParams.page = params.page;
    if (params.size !== undefined) cleanParams.size = params.size;
    if (params.sort && params.sort.trim() !== '') cleanParams.sort = params.sort;
    if (params.orderBy) cleanParams.orderBy = params.orderBy;

    // Include all other parameters (filters) excluding only null, undefined and empty strings
    Object.keys(params).forEach((key) => {
      if (!['page', 'size', 'sort', 'orderBy'].includes(key)) {
        const value = params[key];
        // Include parameter if not null, undefined or empty string
        // Also include 0, false and other valid falsy values
        if (value !== null && value !== undefined && value !== '') {
          cleanParams[key] = value;
        }
      }
    });

    LoggerService.log('getDocuments params:', cleanParams);

    return this.http
      .get<any>(this.baseUrl + `/biggerDiscounts/documentation/page`, { params: cleanParams })
      .pipe(
        tap((response) => LoggerService.log('getDocuments response:', response)),
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError((error) => {
          LoggerService.error('getDocuments error:', error);
          return of(null);
        }),
      );
  }

  uploadDocument(formData: FormData): Observable<any> {
    // For file upload, do not use standard JSON headers
    const auth_token = this.jwtHelper.getToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${auth_token}`,
      // Do not set Content-Type, browser will set it automatically with correct boundary
    });

    return this.http
      .post<MmssDocumentationModel>(
        this.baseUrl + '/biggerDiscounts/documentation/uploadFile',
        formData,
        {
          headers: headers,
        },
      )
      .pipe(
        tap((response) => LoggerService.log(response, 'uploaded mmss documentation')),
        catchError((error) => {
          LoggerService.error('uploadDocument failed!');
          LoggerService.error(error);
          throw error;
        }),
      );
  }

  downloadDocument(documentId: number): Observable<Blob> {
    return this.http.get(
      this.baseUrl + `/biggerDiscounts/documentation/${documentId}/document/download`,
      {
        headers: this.headers,
        responseType: 'blob',
      },
    );
  }

  deleteDocument(documentId: number): Observable<any> {
    return this.http.delete(this.baseUrl + `/biggerDiscounts/documentation/${documentId}`).pipe(
      map((response: any) => {
        LoggerService.log(response, 'deleteDocument');
        return response;
      }),
      catchError((error) => {
        LoggerService.error('deleteDocument failed!');
        LoggerService.error(error);
        throw error;
      }),
    );
  }

  getBrands(): Observable<any> {
    return this.http.get(this.baseUrl + `/biggerDiscounts/documentation/brands`).pipe(
      map((response: any) => {
        LoggerService.log(response, 'getBrands');
        return response;
      }),
    );
  }

  getSourceMmssDocuments(): Observable<any> {
    return this.http.get(this.baseUrl + `/biggerDiscounts/documentation/sourceMmssDocuments`).pipe(
      map((response: any) => {
        LoggerService.log(response, 'getSourceMmssDocuments');
        return response;
      }),
    );
  }

  getMmssDocumentTypes(): Observable<any> {
    return this.http.get(this.baseUrl + `/biggerDiscounts/documentation/mmssDocumentTypes`).pipe(
      map((response: any) => {
        LoggerService.log(response, 'getMmssDocumentTypes');
        return response;
      }),
    );
  }

  getBigBuyersByBrand(brand): Observable<any> {
    return this.http.get(this.baseUrl + `/biggerDiscounts/documentation/big-buyers/` + brand).pipe(
      map((response: any) => {
        LoggerService.log(response, 'getBigBuyersByBrand');
        return response;
      }),
    );
  }

  updateDocument(documentId: number, dto: any): Observable<any> {
    return this.http.put(this.baseUrl + `/biggerDiscounts/documentation/${documentId}`, dto).pipe(
      tap((response) => LoggerService.log(response, 'updateDocument')),
      catchError((error) => {
        LoggerService.error('updateDocument failed!');
        LoggerService.error(error);
        throw error;
      }),
    );
  }
}
